import {Button, Tag} from 'platform/components';
import {Box, Center, Clickable, HStack, Icon, Show, Text, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {isNil} from 'ramda';
import {isNotNilOrEmpty} from 'ramda-adjunct';

import {
  type InterestDecisionReasonCodeValue,
  type InterestRecommendation,
  type InterestRecommendationSignal,
} from '@dms/api/interest-recommendations';
import i18n from '@dms/i18n';

import {getRecommendationChannel} from '../../utils/getRecommendationChannel';
import {getRecommendationSignalLabel} from '../../utils/getRecommendationSignalLabel';
import {getRecommendationSignalReason} from '../../utils/getRecommendationSignalReason';
import {RejectReasonMenu} from '../RejectReasonMenu/RejectReasonMenu';
import {ScoreLevel} from '../ScoreLevel/ScoreLevel';

interface RecommendationRowViewProps {
  recommendation: InterestRecommendation;
  signalCode?: InterestRecommendationSignal['code'];
  vehicleLabel?: string;
  ownerLabel?: string;
  position?: number;
  isSelected?: boolean;
  phase?: 1 | 3;
  isDoneSupported?: boolean;
  actionStatus?: 'idle' | 'pending' | 'uncertain';
  onSelect?: (interestId: string) => void;
  onReject?: (reason: InterestDecisionReasonCodeValue) => void;
  onOpenInterest?: (interestId: string) => void;
  onAction?: (recommendation: InterestRecommendation) => void;
  onDone?: (recommendation: InterestRecommendation, signal: InterestRecommendationSignal) => void;
}

export function RecommendationRowView(props: RecommendationRowViewProps) {
  const signal = props.signalCode
    ? props.recommendation.signals.find((entry) => entry.code === props.signalCode)
    : props.recommendation.signals[0];

  if (isNil(signal)) {
    return null;
  }

  return (
    <RecommendationRowViewContent
      key={`${props.recommendation.interestId}:${signal.code}:${signal.evaluatedAt}`}
      {...props}
      signal={signal}
    />
  );
}

function RecommendationRowViewContent(
  props: RecommendationRowViewProps & {signal: InterestRecommendationSignal}
) {
  const onCustomerClick = props.onSelect ?? props.onOpenInterest;
  const channel = getRecommendationChannel(props.recommendation.recommendedChannel);
  const doneLabel = i18n.t('page.datagrid.labels.done');
  const signalReason = getRecommendationSignalReason(props.signal);
  const isPending = props.actionStatus === 'pending';
  const isUncertain = props.actionStatus === 'uncertain';
  const isActionDisabled = isPending || isUncertain;

  return (
    <Box
      paddingHorizontal={4}
      paddingVertical={3}
      position="relative"
      backgroundColor={props.isSelected ? 'palettes.blue.10.100' : 'general.white'}
    >
      <Show when={props.isSelected}>
        <Box
          position="absolute"
          left={0}
          top={0}
          bottom={0}
          width={1}
          backgroundColor="general.accent"
        />
      </Show>
      <HStack spacing={3} justify="space-between" align="flex-start" wrap>
        <HStack spacing={3} align="flex-start" minWidth={0} grow={1}>
          <Show when={props.position && props.position > 0 ? props.position : null}>
            {(position) => (
              <Box
                width={6}
                height={6}
                flexShrink={0}
                borderRadius="circular"
                backgroundColor={
                  props.isSelected ? 'palettes.blue.20.100' : 'palettes.neutral.30.100'
                }
              >
                <Center height="100%">
                  <Text size="xSmall" color={props.isSelected ? 'link' : 'secondary'} alternative>
                    {position}
                  </Text>
                </Center>
              </Box>
            )}
          </Show>
          <VStack spacing={1} minWidth={0}>
            <HStack spacing={2} align="center" wrap>
              <Show when={isNil(onCustomerClick)}>
                <Text alternative>{props.recommendation.customerLabel}</Text>
              </Show>
              <Show when={onCustomerClick}>
                {(onClick) => (
                  <Clickable
                    role="button"
                    tabIndex={0}
                    aria-pressed={props.onSelect ? Boolean(props.isSelected) : undefined}
                    onClick={() => onClick(props.recommendation.interestId)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        onClick(props.recommendation.interestId);
                      }
                    }}
                  >
                    <Text size="small" alternative>
                      {props.recommendation.customerLabel}
                    </Text>
                  </Clickable>
                )}
              </Show>
              <Show when={isNotNilOrEmpty(props.vehicleLabel) ? props.vehicleLabel : null}>
                {(label) => (
                  <Text size="small" color="secondary">
                    {label}
                  </Text>
                )}
              </Show>
              <Show when={isNotNilOrEmpty(props.ownerLabel) ? props.ownerLabel : null}>
                {(label) => (
                  <Text size="xSmall" color="secondary">
                    {label}
                  </Text>
                )}
              </Show>
            </HStack>
            <HStack spacing={2} align="center" wrap>
              <Button
                variant="link"
                size="small"
                leftIcon={channel.icon}
                title={channel.label}
                aria-label={channel.label}
                isDisabled={isActionDisabled || isNil(props.onAction)}
                onClick={() => props.onAction?.(props.recommendation)}
              />
              <Text size="xSmall" color="secondary">
                {channel.reason}
              </Text>
            </HStack>
            <HStack spacing={2} align="center" wrap>
              <Icon value="action/info_outline" size={3} color="palettes.purple.60.100" />
              <Tag
                label={getRecommendationSignalLabel(props.signal)}
                size="small"
                isSubtle
                colorScheme={match(props.signal.severity)
                  .with('danger', () => 'red' as const)
                  .with('warning', () => 'orange' as const)
                  .with('info', () => 'blue' as const)
                  .exhaustive()}
              />
              <Show when={isNotNilOrEmpty(signalReason) ? signalReason : null}>
                {(reason) => (
                  <Text size="xSmall" color="secondary">
                    {reason}
                  </Text>
                )}
              </Show>
              <Show when={props.signal.evidenceIncomplete}>
                <Text size="xSmall" color="secondary">
                  {i18n.t('entity.interestRecommendations.labels.incompleteEvidence')}
                </Text>
              </Show>
            </HStack>
            <Show when={isUncertain}>
              <Text role="status" size="small" color="secondary">
                {i18n.t('entity.interestRecommendations.labels.actionUncertain')}
              </Text>
            </Show>
            <Show when={isPending && !isUncertain}>
              <Text role="status" size="small" color="secondary">
                {i18n.t('entity.interestRecommendations.labels.actionPending')}
              </Text>
            </Show>
          </VStack>
        </HStack>
        <Show
          when={
            props.phase === 3 ||
            Boolean(props.isDoneSupported && props.onDone) ||
            Boolean(props.onReject)
          }
        >
          <VStack spacing={3} align="flex-end">
            <Show when={props.phase === 3}>
              <ScoreLevel score={props.recommendation.score} />
            </Show>
            <HStack spacing={2}>
              <Show when={props.isDoneSupported && props.onDone}>
                <Button
                  size="small"
                  variant="secondary"
                  leftIcon="navigation/check"
                  title={doneLabel}
                  aria-label={doneLabel}
                  isDisabled={isActionDisabled}
                  isLoading={isPending}
                  onClick={() => props.onDone?.(props.recommendation, props.signal)}
                />
              </Show>
              <Show when={props.onReject}>
                {(onReject) => (
                  <RejectReasonMenu
                    isDisabled={isActionDisabled}
                    isLoading={isPending}
                    onReject={onReject}
                  />
                )}
              </Show>
            </HStack>
          </VStack>
        </Show>
      </HStack>
    </Box>
  );
}
